import React, { useState, useEffect } from 'react';
import { usePIM } from '../../context/PIMContext';
import { GhostButton } from '../ui/GhostButton';
import { Input } from '../ui/Input';
import { Search, Settings, RefreshCw, Trash2, Star, Plus } from 'lucide-react';
import { AddressManagementModal } from '../modals/AddressManagementModal';
import { AddressAssignmentModal } from '../modals/AddressAssignmentModal';

import { useSortableData } from '../../hooks/useSortableData';
import { SortableHeader } from '../ui/SortableHeader';

export const AddressTab = () => {
    const { productData } = usePIM();
    const [addresses, setAddresses] = useState([]);
    const [search, setSearch] = useState('');
    const [isManageModalOpen, setIsManageModalOpen] = useState(false);
    const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);

    const sku = productData?.sku;

    const fetchAddresses = React.useCallback(async () => {
        if (!sku) return;
        try {
            const res = await fetch(`/api/products/${sku}/addresses`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                const data = await res.json();
                setAddresses(data);
            }
        } catch (err) {
            console.error("Error fetching assigned addresses:", err);
        }
    }, [sku]);

    useEffect(() => {
        if (!sku) return;

        let active = true;
        const load = async () => {
            try {
                const res = await fetch(`/api/products/${sku}/addresses`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                if (res.ok && active) {
                    const data = await res.json();
                    setAddresses(data);
                }
            } catch (err) {
                if (active) console.error("Error fetching assigned addresses:", err);
            }
        };

        load();

        return () => { active = false; };
    }, [sku]);

    const handleUnassign = async (addressId) => {
        try {
            const res = await fetch(`/api/products/${productData.sku}/addresses/${addressId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) fetchAddresses();
        } catch (err) {
            console.error("Error unassigning address:", err);
        }
    };

    const handleSetDefault = async (addressId, isDefault) => {
        try {
            const res = await fetch(`/api/products/${productData.sku}/addresses/${addressId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ is_default: isDefault })
            });
            if (res.ok) fetchAddresses();
        } catch (err) {
            console.error("Error setting default address:", err);
        }
    };

    const filteredAddresses = addresses.filter(addr =>
        addr.company_name.toLowerCase().includes(search.toLowerCase()) ||
        addr.city.toLowerCase().includes(search.toLowerCase()) ||
        addr.country.toLowerCase().includes(search.toLowerCase())
    );

    // Sorting
    const { items: sortedAddresses, requestSort, sortConfig } = useSortableData(filteredAddresses);

    return (
        <div className="h-full flex flex-col gap-4">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-white">Address Management</h2>
                <div className="flex gap-2">
                    <GhostButton onClick={() => setIsAssignmentModalOpen(true)}>
                        <Plus size={16} /> Add Address
                    </GhostButton>
                    <GhostButton onClick={() => setIsManageModalOpen(true)}>
                        <Settings size={16} /> Manage Addresses
                    </GhostButton>
                </div>
            </div>

            <div className="flex gap-2">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-500" size={16} />
                    <Input
                        placeholder="Search assigned addresses..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10 bg-stone-900 border-stone-700 w-full"
                    />
                </div>
            </div>

            <div className="flex-1 bg-stone-900/50 border border-stone-700 rounded-xl overflow-hidden flex flex-col">
                <div className="overflow-y-auto flex-1 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-stone-950 [&::-webkit-scrollbar-thumb]:bg-stone-700 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-stone-600">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-stone-900 text-stone-500 text-xs font-bold uppercase sticky top-0 z-10">
                            <tr>
                                <SortableHeader label="Default" sortKey="is_default" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Company" sortKey="company_name" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Contact" sortKey="first_name" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Email" sortKey="email" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Address" sortKey="street" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Type" sortKey="type" currentSort={sortConfig} onSort={requestSort} />
                                <th className="p-4 border-b border-stone-800 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-800 text-sm text-stone-300">
                            {sortedAddresses.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="p-8 text-center text-stone-500">
                                        No addresses assigned to this product.
                                    </td>
                                </tr>
                            ) : (
                                sortedAddresses.map(addr => (
                                    <tr key={addr.link_id} className="hover:bg-stone-800/50 transition-colors group">
                                        <td className="px-4 py-0.5">
                                            <button
                                                onClick={() => handleSetDefault(addr.id, !addr.is_default)}
                                                className={`transition ${addr.is_default ? 'text-yellow-400' : 'text-stone-600 hover:text-yellow-400'}`}
                                            >
                                                <Star size={18} fill={addr.is_default ? "currentColor" : "none"} />
                                            </button>
                                        </td>
                                        <td className="px-4 py-0.5 font-medium text-white">{addr.company_name}</td>
                                        <td className="px-4 py-0.5">{addr.first_name} {addr.last_name}</td>
                                        <td className="px-4 py-0.5 text-stone-400">{addr.email || '—'}</td>
                                        <td className="px-4 py-0.5">
                                            {addr.street} {addr.number}, {addr.postal_code} {addr.city}, {addr.country}
                                        </td>
                                        <td className="px-4 py-0.5">
                                            <span className={`px-2 py-1 rounded text-xs font-medium border ${addr.type === 'Main' ? 'bg-purple-900/20 border-purple-800 text-white' :
                                                addr.type === 'Supplier' ? 'bg-blue-900/20 border-blue-800 text-white' :
                                                    'bg-stone-800 border-stone-700 text-white'
                                                }`}>
                                                {addr.type}
                                            </span>
                                        </td>
                                        <td className="px-4 py-0.5 text-right">
                                            <button
                                                onClick={() => handleUnassign(addr.id)}
                                                className="p-1.5 rounded hover:bg-red-900/30 text-stone-400 hover:text-red-500 transition opacity-0 group-hover:opacity-100"
                                                title="Unassign"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <AddressManagementModal
                isOpen={isManageModalOpen}
                onClose={() => setIsManageModalOpen(false)}
            />

            <AddressAssignmentModal
                isOpen={isAssignmentModalOpen}
                onClose={() => setIsAssignmentModalOpen(false)}
                productSku={productData?.sku}
                onUpdate={fetchAddresses}
            />
        </div>
    );
};
